import { CheckCircle2, Eye, RefreshCw, Save, Sparkles, Trash2 } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AdminApi, PromptScope } from "@/api";
import { Badge, Button, Disclosure, SelectField, TextField, TextareaField } from "@/components/ui";
import type { LlmModelOption, PromptPreview, PromptVersion, PromptVersionList } from "@/types";
import { modelLabel, scanModels } from "@/lib/llmModels";
import { diffLines, hasChanges } from "@/utils/textDiff";
import type { PromptTargetSpec } from "./promptTargets";
import { TextDiffView } from "./TextDiffView";

// A freshly saved version's summary is written by a background task; poll the
// list a few times so it appears without a manual reload.
const SUMMARY_POLL_MS = 5000;
const SUMMARY_POLL_TRIES = 12;

/**
 * Versions of one managed prompt: list, edit from a base version, save a new
 * inactive version, mark active, soft delete and preview templates. Serving
 * targets switch on activation; pipeline executions explicitly pin a version.
 */
export function PromptVersionsPanel({
  api,
  scope,
  spec,
  title,
  onFlash,
  onError,
  initialVersionId,
  initialContent,
  footer,
}: {
  api: AdminApi;
  scope: PromptScope;
  spec: PromptTargetSpec;
  title?: string;
  onFlash: (message: string) => void;
  onError: (error: unknown) => void;
  initialVersionId?: string | null;
  initialContent?: Record<string, string>;
  /** Shown at the end of the open panel (the prompt's call sites). */
  footer?: ReactNode;
}) {
  const [list, setList] = useState<PromptVersionList | null>(null);
  const [baseId, setBaseId] = useState<string>("");
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [previews, setPreviews] = useState<PromptPreview[] | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [models, setModels] = useState<LlmModelOption[]>([]);
  const pollTries = useRef(0);

  const versions = list?.versions ?? [];
  const active = versions.find((version) => version.status === "active") ?? null;
  const base = versions.find((version) => version.id === baseId) ?? null;

  const loadBase = useCallback(
    (version: PromptVersion | null) => {
      setBaseId(version?.id ?? "");
      setDraft(
        Object.fromEntries(spec.fields.map((field) => [field.name, version?.content[field.name] ?? initialContent?.[field.name] ?? ""])),
      );
      setDescription("");
      setPreviews(null);
      setPreviewError(null);
    },
    [spec.fields, initialContent],
  );

  const reload = useCallback(
    async (keepEditor = true) => {
      const next = await api.listPromptVersions(scope);
      setList(next);
      if (!keepEditor) {
        loadBase(next.versions.find((version) => version.id === (initialVersionId || (!initialContent ? next.latest_version_id : null))) ?? null);
      }
      return next;
    },
    [api, scope, loadBase, initialVersionId, initialContent],
  );

  useEffect(() => {
    reload(false).catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(scope)]);

  useEffect(() => {
    if (!spec.fields.some((field) => field.input === "model")) return;
    api
      .listLlmModels()
      .then((json) => setModels(spec.detectorModels
        ? scanModels(json.models)
        : json.models.filter((m) => m.is_enabled && (spec.registryModels
          ? ["gemini", "openrouter", "enaible"].includes(m.transport)
          : m.transport === "gemini"))))
      .catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec.target]);

  // Poll while a version is still waiting for its change summary.
  const waitingForSummary = versions.some(
    (version) => version.base_version_id && !version.change_summary && isRecent(version),
  );
  useEffect(() => {
    if (!waitingForSummary || pollTries.current >= SUMMARY_POLL_TRIES) return;
    const timer = window.setTimeout(() => {
      pollTries.current += 1;
      reload().catch(() => undefined);
    }, SUMMARY_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [waitingForSummary, list, reload]);

  const diffs = useMemo(
    () =>
      spec.fields
        .filter((field) => field.input !== "model")
        .map((field) => ({
          field,
          lines: diffLines(base?.content[field.name] ?? "", draft[field.name] ?? ""),
        })),
    [spec.fields, base, draft],
  );
  const dirty =
    Boolean(spec.outputContract && base?.content.output_contract !== spec.outputContract) ||
    diffs.some((diff) => hasChanges(diff.lines)) ||
    spec.fields.some(
      (field) => field.input === "model" && (draft[field.name] ?? "") !== (base?.content[field.name] ?? ""),
    );
  const missingRequired = spec.fields.some((field) => !field.optional && !(draft[field.name] ?? "").trim());

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  const save = () =>
    run(async () => {
      const content = Object.fromEntries(
        spec.fields.map((field) => {
          const value = draft[field.name] ?? "";
          return [field.name, field.optional && !value.trim() ? null : value];
        }),
      );
      if (spec.outputContract) content.output_contract = spec.outputContract;
      const saved = await api.savePromptVersion(scope, {
        content,
        description: description.trim() || null,
        base_version_id: baseId || null,
      });
      pollTries.current = 0;
      const next = await reload();
      loadBase(next.versions.find((version) => version.id === saved.id) ?? null);
      onFlash(spec.explicitSelection ? `${spec.label} v${saved.version}을 저장했습니다. 실행할 때 해당 버전을 선택하세요.`
        : `Saved ${spec.label} v${saved.version}. It is not applied yet.`);
    });

  const apply = (version: PromptVersion) => {
    if (spec.outputContract && version.content.output_contract !== spec.outputContract) return;
    const confirmation = spec.explicitSelection
      ? `${spec.label} v${version.version}에 활성 표시를 설정할까요? 실행은 명시적으로 선택한 버전을 사용합니다. 기존 작업과 자동 실행 설정은 바뀌지 않습니다.`
      : `Apply ${spec.label} v${version.version}? It starts serving immediately.`;
    if (!window.confirm(confirmation)) return;
    return run(async () => {
      setList(await api.activatePromptVersion(scope, version.id));
      onFlash(spec.explicitSelection ? `${spec.label} v${version.version}에 활성 표시를 설정했습니다. 실행에는 버전을 별도로 선택하세요.`
        : `${spec.label} v${version.version} is now serving.`);
    });
  };

  const remove = (version: PromptVersion) => {
    if (!window.confirm(`Delete ${spec.label} v${version.version}? It is hidden, not erased.`)) return;
    return run(async () => {
      setList(await api.deletePromptVersion(scope, version.id));
      if (version.id === baseId) loadBase(null);
      onFlash(`Deleted ${spec.label} v${version.version}.`);
    });
  };

  const resummarize = (version: PromptVersion) =>
    run(async () => {
      await api.resummarizePromptVersion(scope, version.id);
      pollTries.current = 0;
      await reload();
    });

  const preview = () =>
    run(async () => {
      if (!("target" in scope)) return;
      setPreviewError(null);
      try {
        const content: Record<string, string | null> = spec.criteriaPreview
          ? { body: draft.body ?? "", model: draft.model || null, output_contract: spec.outputContract ?? null }
          : { body: draft.body ?? "" };
        const json = await api.previewPromptDraft(scope.target, content);
        setPreviews(json.previews);
      } catch (error) {
        setPreviews(null);
        setPreviewError(error instanceof Error ? error.message : String(error));
      }
    });

  const baseOptions = versions.map((version) => ({
    value: version.id,
    label: `v${version.version}${tags(version, list, spec.explicitSelection).map((tag) => ` (${tag})`).join("")}`,
  }));

  return (
    <Disclosure
      title={`${title ?? spec.label} · ${spec.explicitSelection ? active ? `활성 표시 v${active.version}` : "활성 표시 없음" : active ? `serving v${active.version}` : "nothing serving"}`}
      subtitle={spec.help}
      defaultOpen={false}
    >
      <div className="grid gap-4">
        <VersionTable
          versions={versions}
          list={list}
          busy={busy}
          baseId={baseId}
          onEdit={loadBase}
          onApply={apply}
          onDelete={remove}
          onResummarize={resummarize}
          expectedOutputContract={spec.outputContract}
          explicitSelection={spec.explicitSelection}
        />

        <div className="grid gap-3 rounded-lg border border-border p-3.5">
          {spec.outputContract && <p className="m-0 text-sm">출력 계약: <code>{spec.outputContract}</code>. 본문을 이 출력 형식에 맞춰 저장하세요. 계약 선언이 없거나 다른 기존 버전은 새 버전으로 저장해야 적용할 수 있습니다.</p>}
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label="Edit from (base version)"
              value={baseId}
              onChange={(id) => loadBase(versions.find((version) => version.id === id) ?? null)}
              options={[{ value: "", label: "(empty — first version)" }, ...baseOptions]}
            />
            <TextField label="Description" value={description} onChange={setDescription} />
          </div>
          {base && active && base.id !== active.id && (
            <p className="m-0 text-sm text-muted-foreground">
              {spec.explicitSelection ? `편집 기준은 v${base.version}이며 활성 표시 버전은 v${active.version}입니다. 변경 요약은 v${base.version}과 비교합니다.`
                : `Base v${base.version} is not the serving version (v${active.version}). The change summary will compare against v${base.version}.`}
            </p>
          )}
          {spec.fields.map((field) =>
            field.input === "model" ? (
              <SelectField
                key={field.name}
                label={field.label}
                value={draft[field.name] ?? ""}
                onChange={(value) => setDraft({ ...draft, [field.name]: value })}
                options={[
                  { value: "", label: spec.defaultModelLabel ?? "Server default (GEMINI_MODEL)" },
                  // A Qwen without its key stays choosable: the version is configuration, and a run says why it stops.
                  ...models.map((model) => ({ value: model.id, label: `${spec.detectorModels ? modelLabel(model) : model.label} · ${model.id}` })),
                ]}
              />
            ) : (
              <TextareaField
                key={field.name}
                label={field.optional ? `${field.label} (optional)` : field.label}
                value={draft[field.name] ?? ""}
                onChange={(value) => setDraft({ ...draft, [field.name]: value })}
                rows={field.rows ?? 12}
                mono
              />
            ),
          )}
          {spec.reviewerPrompt && <p className="m-0 text-xs text-muted-foreground">
            여기 저장하는 모델은 Gemini 경로에서 사용합니다. 1.1차의 Claude CLI·Qwen 경로는 서버 실행 설정을 사용하므로,
            실행 모델을 지정하려면 후보 탐지 화면에서 선택하고 미리보기에서 확인하세요.
          </p>}
          {dirty && (
            <Disclosure title={`Changes against ${base ? `v${base.version}` : "empty"}`} defaultOpen={false}>
              {diffs
                .filter((diff) => hasChanges(diff.lines))
                .map((diff) => (
                  <TextDiffView key={diff.field.name} title={diff.field.label} lines={diff.lines} />
                ))}
            </Disclosure>
          )}
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy || !dirty || missingRequired} onClick={save}>
              <Save /> Save as new version
            </Button>
            {(spec.template || spec.criteriaPreview) && "target" in scope && (
              <Button variant="outline" disabled={busy} onClick={preview}>
                <Eye /> {spec.criteriaPreview ? "미리보기 / 출력 검사" : "Preview rendered prompt"}
              </Button>
            )}
          </div>
          <p className="m-0 text-xs text-muted-foreground">
            {spec.explicitSelection ? "활성 표시는 관리 목록의 표시입니다. 실행에는 선택한 버전을 고정하며, 기존 작업과 자동 실행 설정은 바뀌지 않습니다. 비활성 버전도 명시적으로 선택할 수 있습니다."
              : "Saving never changes what serves. Use Apply on a version to put it live."}
          </p>
          {previewError && <p className="m-0 text-sm text-destructive">Preview failed: {previewError}</p>}
          {previews?.map((item) => (
            <Disclosure key={item.key} title={item.title} subtitle={item.description} defaultOpen={false}>
              <pre className="block max-w-full overflow-x-hidden rounded-lg border border-border bg-surface-input p-3 font-mono whitespace-pre-wrap text-code-foreground wrap-anywhere">
                {item.body}
              </pre>
            </Disclosure>
          ))}
        </div>
        {footer}
      </div>
    </Disclosure>
  );
}

function VersionTable({
  versions,
  list,
  busy,
  baseId,
  onEdit,
  onApply,
  onDelete,
  onResummarize,
  expectedOutputContract,
  explicitSelection,
}: {
  versions: PromptVersion[];
  list: PromptVersionList | null;
  busy: boolean;
  baseId: string;
  onEdit: (version: PromptVersion) => void;
  onApply: (version: PromptVersion) => void;
  onDelete: (version: PromptVersion) => void;
  onResummarize: (version: PromptVersion) => void;
  expectedOutputContract?: string;
  explicitSelection?: boolean;
}) {
  if (!versions.length) {
    return <p className="m-0 text-sm text-muted-foreground">No versions yet.</p>;
  }
  return (
    <div className="grid gap-2">
      {versions.map((version) => (
        <div
          key={version.id}
          data-testid={`prompt-version-${version.version}`}
          className={`grid gap-1.5 rounded-lg border p-3 ${version.id === baseId ? "border-ring" : "border-border"}`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <strong>v{version.version}</strong>
            {tags(version, list, explicitSelection).map((tag) => (
              <Badge key={tag} size="compact" variant={tag === "serving" || tag === "활성 표시" ? "accent" : "default"}>
                {tag}
              </Badge>
            ))}
            {version.base_version != null && (
              <small className="text-xs text-muted-foreground">from v{version.base_version}</small>
            )}
            <small className="text-xs text-muted-foreground">{formatDate(version.created_at)}</small>
            <span className="ml-auto flex flex-wrap gap-1.5">
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => onEdit(version)}>
                Edit from
              </Button>
              {version.status !== "active" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy || version.syntax === "legacy" || Boolean(expectedOutputContract && version.content.output_contract !== expectedOutputContract)}
                  title={expectedOutputContract && version.content.output_contract !== expectedOutputContract ? "출력 계약을 확인해 새 버전으로 저장하세요." : version.syntax === "legacy" ? "Saved before the Jinja conversion; re-save its text to apply it." : undefined}
                  onClick={() => onApply(version)}
                >
                  <CheckCircle2 /> {explicitSelection ? "활성 표시" : "Apply"}
                </Button>
              )}
              {version.status !== "active" && (
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => onDelete(version)}>
                  <Trash2 /> Delete
                </Button>
              )}
            </span>
          </div>
          {version.description && <p className="m-0 text-sm">{version.description}</p>}
          <SummaryLine version={version} busy={busy} onResummarize={onResummarize} />
        </div>
      ))}
    </div>
  );
}

function SummaryLine({
  version,
  busy,
  onResummarize,
}: {
  version: PromptVersion;
  busy: boolean;
  onResummarize: (version: PromptVersion) => void;
}) {
  if (!version.base_version_id) return null;
  if (version.change_summary) {
    return (
      <div className="flex items-start gap-2">
        <Sparkles className="mt-0.5 size-3.75 shrink-0 text-muted-foreground" />
        <p className="m-0 text-sm whitespace-pre-wrap text-muted-foreground">{version.change_summary}</p>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Sparkles className="size-3.75" />
      {isRecent(version) ? "Summarizing the change…" : "No summary."}
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => onResummarize(version)}>
        <RefreshCw /> Summarize again
      </Button>
    </div>
  );
}

function tags(version: PromptVersion, list: PromptVersionList | null, explicitSelection = false): string[] {
  const out: string[] = [];
  if (version.status === "active") out.push(explicitSelection ? "활성 표시" : "serving");
  if (list?.latest_version_id === version.id) out.push("latest");
  if (version.syntax === "legacy") out.push("pre-Jinja");
  return out;
}

function isRecent(version: PromptVersion): boolean {
  if (!version.created_at) return false;
  return Date.now() - new Date(version.created_at).getTime() < 3 * 60 * 1000;
}

function formatDate(value?: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleString();
}
